import { type ChangeEvent, type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'
import { ErrorMessage, useField, useFormikContext } from 'formik'
// eslint-disable-next-line @dhl/generic/no-restricted-imports -- Use sx props or styled components to create CSS
import { makeStyles } from 'tss-react/mui'
import { useTranslation } from 'react-i18next'
import { Badge, Box, Fab, IconButton, InputAdornment, TextField, type Theme, Typography } from '@mui/material'
import { useSnackbar } from '@dhl/snackbar'
import { useFocusOnFirstError } from '@dhl/use-focus-on-first-error'
import { Close, PieceMedium, Camera } from '@dhl/icons'
import { identity, useDeviceModel } from '@dhl/utils'
import type { InputMode, AfterScanAction, BarcodeScannerFormat, BarcodeScannerFormatKind } from '../types'
import { useBarcodeListener } from '../provider/BarcodeLaserProvider'
import { withPickupCode, cleanupBarcode, findFormatKind } from '../helpers'
import { BarcodeCameraScanner } from '../components/BarcodeCameraScanner'
import { BarcodeScannerErrorMessage } from '../components/BarcodeScannerErrorMessage'
import { ScanOptionsDialog } from '../components/ScanOptionsDialog'

const useStyles = makeStyles()((theme: Theme) => ({
  closeIcon: {
    position: 'absolute',
    zIndex: 2,
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  title: {
    position: 'absolute',
    left: '50%',
    top: 0,
    transform: 'translate(-50%, 0)',
    height: '56px',
    lineHeight: '56px',
    textTransform: 'uppercase',
    color: theme.palette.common.white,
    whiteSpace: 'nowrap',
  },
  doneIcon: {
    position: 'fixed',
    zIndex: 1300,
    right: theme.spacing(2),
    bottom: theme.spacing(2),
  },
  white: {
    color: theme.palette.common.white,
  },
  // TODO: is it used?
  switchIcon: {
    position: 'absolute',
    zIndex: 2,
    left: theme.spacing(2),
    bottom: theme.spacing(2),
  },
  // TODO: is it used?
  switchIconLaserUI: {
    bottom: '100px',
  },
  // TODO: is it used?
  buttonGroup: {
    top: 56,
    position: 'absolute',
  },
}))

export type BarcodeInputProps = {
  name: string
  namePickupCode?: string
  counter?: number
  label?: string
  title?: string
  autoFocus?: boolean
  className?: string
  laserScannerEnabled?: boolean
  canScanQrCode?: boolean
  barcodePattern?: string
  hasClearOption?: boolean
  hideScannerIcon?: boolean
  isOrientationLockEnabled?: boolean
  isHandheldScanner: boolean
  variant?: 'outlined' | 'filled'
  formats?: BarcodeScannerFormat[]
  lockedLocation?: boolean
  getAfterScanAction?: (
    /**
     * The scanned barcode
     */
    currentBarcode?: string,

    /**
     * The possible previous barcode, this gives room for consumers to reset a barcode for example when it is not
     * what was expected to retrieve (for example in printless customer hand in)
     */
    previousBarcode?: string
  ) => AfterScanAction
  /**
   * option to parse what comes in as "barcode"
   */
  parseScan?: (value: string) => string

  onDone?: () => void

  /**
   * When you need to do stuff when person clears the barcode
   */
  onClear?: () => void

  inputHelperText?: string
}

const defaultGetAfterScanAction: () => AfterScanAction = () => 'show-form'

const BarcodeInputInner = ({
  name,
  namePickupCode,
  counter,
  label = 'Barcode',
  title = 'Scan',
  autoFocus = false,
  className = '',
  laserScannerEnabled = true,
  canScanQrCode = false,
  barcodePattern = '.*',
  hasClearOption = true,
  hideScannerIcon = false,
  isOrientationLockEnabled = false,
  isHandheldScanner,
  variant = 'outlined',
  lockedLocation = false,
  formats = ['code_39', 'code_128', 'itf'],
  onDone,
  onClear,
  getAfterScanAction = defaultGetAfterScanAction,
  parseScan = identity,
  inputHelperText = '',
}: BarcodeInputProps) => {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const { classes, cx } = useStyles()
  const textInputRef = useRef<HTMLInputElement>(null)
  const getFormats = (): BarcodeScannerFormat[] => (canScanQrCode ? ['code_39', 'code_128', 'itf', 'qr_code'] : formats)

  const [inputMode, setInputMode] = useState<InputMode>('manual')
  const [scannerFailure, setScannerFailure] = useState(false)

  const formatKind = findFormatKind(formats) || '1D'
  const [selectedScanFormat, setSelectedScanFormat] = useState<BarcodeScannerFormatKind>(formatKind)
  const [displayScanOptionsDialog, setDisplayScanOptionsDialog] = useState<boolean>(false)
  const [field, meta] = useField<string>(name)
  const { isSubmitting, handleSubmit, setFieldValue, setFieldError, setFieldTouched } = useFormikContext()
  const { barcode } = useBarcodeListener()

  // Warn any consumers of this input field if they want to listen to laser scanner that they need to include
  // the provider in their app
  if (barcode === null && laserScannerEnabled) {
    // eslint-disable-next-line no-console
    console.warn(
      'The component BarcodeInput requires the BarcodeLaserListenerProvider in order to function with a laser scanner'
    )
  }

  const onCloseScanOptionsDialog = () => setDisplayScanOptionsDialog(false)

  const onSelectScanOption = (selectedOption: BarcodeScannerFormatKind) => {
    setSelectedScanFormat(selectedOption)
    setInputMode('camera')
    setDisplayScanOptionsDialog(false)
  }

  useFocusOnFirstError(name, textInputRef, textInputRef)

  const setInputField = useCallback(
    (value = '', revalidate = false, isTouched = false) => {
      setFieldTouched(name, isTouched, false)
      setFieldError(name, '')

      setFieldValue(name, value, revalidate)
    },
    [name, setFieldError, setFieldTouched, setFieldValue]
  )

  const handleProgrammaticChange = useCallback(
    (value: string) => {
      if (lockedLocation && !!field.value) {
        enqueueSnackbar(t('components.inputs.barcodeInput.fixedLocationEnabled'), {
          variant: 'error',
        })
        return
      }

      const trimmedValue = value.trim()
      const code = withPickupCode(trimmedValue)

      // FIXME: our kafka messages still has spaces (so pickup message with barcode can have a space),
      // its a backend bug but we suffer now from it so thats why we trim two times one from input
      // (our barcode scanner can be any scanner) and one time from pickup code (see message above backend error)
      const inputBarcode = code ? code.parcelId.trim() : trimmedValue

      if (namePickupCode) {
        setFieldValue(namePickupCode, code?.pickupCode || '')
      }

      setInputField(inputBarcode, true, true)

      switch (getAfterScanAction(trimmedValue, field.value)) {
        case 'submit-form':
          setInputMode('manual')
          setTimeout(handleSubmit, 100)
          break
        case 'show-form':
        default:
          setInputMode('manual')
          setTimeout(() => textInputRef.current?.blur(), 0)
          break
      }
    },
    [
      namePickupCode,
      lockedLocation,
      field.value,
      getAfterScanAction,
      setInputField,
      setFieldValue,
      setInputMode,
      handleSubmit,
      enqueueSnackbar,
      t,
    ]
  )

  /**
   * LASER scanner input
   * TODO: we need to do this differently, as consumer i have no influence on what it will do, if i
   * also want to store something else from this code (say a pickup code) it is not possible...
   */
  useEffect(() => {
    if (barcode) {
      const parsedBarcode = parseScan(barcode || '')

      if (
        parsedBarcode &&
        field.value !== parsedBarcode &&
        new RegExp(barcodePattern).test(parsedBarcode.toUpperCase())
      ) {
        handleProgrammaticChange(parsedBarcode)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcode, barcodePattern, handleProgrammaticChange])

  const handleSwitchCameraClick = () => {
    // Clear existing errors
    setFieldError(name, '')

    if (canScanQrCode) {
      setDisplayScanOptionsDialog(true)
      return
    }

    setInputMode('camera')
  }

  const handleManualChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = cleanupBarcode(event.target.value).toUpperCase()

    if (new RegExp(barcodePattern).test(newValue) || inputMode === 'manual') {
      setInputField(newValue, true, true)
    }
  }

  const handleManualKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    setInputMode('manual')

    if (event.key === 'Enter') {
      handleSubmit()
      event.preventDefault()
    }
  }

  const handleClear = () => {
    setInputField('')
    setScannerFailure(false)
    onClear?.()
  }

  const handleError = () => {
    setScannerFailure(true)
  }

  return (
    <>
      {(inputMode === 'manual' || inputMode === 'laser') && (
        <Box>
          <TextField
            className={className}
            variant={variant}
            name={field.name}
            value={field.value}
            onBlur={() => field.onBlur}
            onChange={handleManualChange}
            onKeyUp={handleManualKeyPress}
            inputRef={textInputRef}
            autoFocus={autoFocus}
            label={label}
            autoComplete="off"
            autoCorrect="off"
            data-test={`barcode-input-${name}`}
            data-testid={`barcode-input-${name}`}
            error={meta.touched && !!meta.error}
            helperText={(meta.touched && meta.error) || inputHelperText}
            disabled={isSubmitting || lockedLocation}
            fullWidth
            slotProps={{
              input: {
                endAdornment: lockedLocation ? null : (
                  <InputAdornment position="end" sx={{ height: '100% !important' }}>
                    {hasClearOption && field.value !== '' && (
                      <IconButton data-testid="reset" disabled={isSubmitting} onClick={() => handleClear()}>
                        <Close />
                      </IconButton>
                    )}
                    {!hideScannerIcon && !isHandheldScanner && field.value === '' && (
                      <IconButton
                        data-test="open-camera"
                        disabled={isSubmitting}
                        onClick={() => handleSwitchCameraClick()}
                      >
                        <Camera />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      )}
      {inputMode === 'camera' && (
        <>
          {!scannerFailure && (
            <>
              <BarcodeCameraScanner
                onChange={handleProgrammaticChange}
                onError={handleError}
                // TODO: is this an error?
                barcodePattern=".*"
                isActive={!isSubmitting && !(meta.touched && !!meta.error)}
                formats={getFormats()}
                isOrientationLockEnabled={isOrientationLockEnabled}
                parseScan={parseScan}
                selectedScanFormat={selectedScanFormat}
              >
                <Typography className={classes.title} variant="h6">
                  {title}
                </Typography>

                <IconButton
                  onClick={() => setInputMode('manual')}
                  className={cx(classes.white, classes.closeIcon)}
                  data-test="close-camera"
                >
                  <Close />
                </IconButton>

                {Number(counter) > 0 && (
                  <Badge badgeContent={counter} color="primary" className={classes.doneIcon} data-test="counter-badge">
                    <Fab aria-label="Add" onClick={onDone} data-test="done-button">
                      <PieceMedium />
                    </Fab>
                  </Badge>
                )}
              </BarcodeCameraScanner>

              <ErrorMessage name={name}>
                {(message: string) => (
                  <BarcodeScannerErrorMessage onClose={handleClear} barcode={field.value}>
                    {message}
                  </BarcodeScannerErrorMessage>
                )}
              </ErrorMessage>
            </>
          )}

          {scannerFailure && (
            <BarcodeScannerErrorMessage onClose={handleClear} barcode={field.value}>
              {t('components.inputs.barcodeInput.barcodeInputError')}
            </BarcodeScannerErrorMessage>
          )}
        </>
      )}
      {displayScanOptionsDialog && (
        <ScanOptionsDialog
          isOpen={displayScanOptionsDialog}
          onClose={onCloseScanOptionsDialog}
          onSelect={onSelectScanOption}
        />
      )}
    </>
  )
}

/**
 * Our barcode input which is wired to Formik
 * used in the applications: Spa app, be carefull when making changes!
 *
 * @deprecated MUI-based implementation kept for projects that don't yet depend on `@dhl-ui/components`.
 * Prefer `BarcodeInput` from `@dhl/barcode-input` once it has been migrated to the DHL Design System (DSS).
 */
export const BarcodeInputDeprecated = (props: Omit<BarcodeInputProps, 'isHandheldScanner'>) => {
  const { isHandheldScanner } = useDeviceModel()

  if (isHandheldScanner === undefined) {
    return null
  }

  return <BarcodeInputInner {...props} isHandheldScanner={isHandheldScanner} />
}
